const VIEW_AUTHORING_MUTATION_TOOL_NAMES = new Set([
  'create_ontology_view_definition',
  'patch_view_contract',
  'update_ontology_view_definition',
  'delete_ontology_view_definition',
]);
const EXPORT_TOOL_NAME = 'export_to_database';

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toEpochMs = (value) => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeViewDefinition = (viewDefinition) => {
  if (!viewDefinition || typeof viewDefinition !== 'object') {
    return null;
  }
  const id = viewDefinition.id ? String(viewDefinition.id) : '';
  if (!id) {
    return null;
  }
  return {
    ...viewDefinition,
    id,
    organization_id: viewDefinition.organization_id ? String(viewDefinition.organization_id) : null,
    previous_version_id: viewDefinition.previous_version_id ? String(viewDefinition.previous_version_id) : null,
    visible_to_roles: safeArray(viewDefinition.visible_to_roles),
    view_schema:
      viewDefinition.view_schema && typeof viewDefinition.view_schema === 'object' ? viewDefinition.view_schema : {},
    properties:
      viewDefinition.properties && typeof viewDefinition.properties === 'object' ? viewDefinition.properties : {},
  };
};

const sortViewsByRecency = (views) =>
  [...views].sort((left, right) => {
    const leftTime = Math.max(toEpochMs(left?.updated_at), toEpochMs(left?.created_at));
    const rightTime = Math.max(toEpochMs(right?.updated_at), toEpochMs(right?.created_at));
    return rightTime - leftTime;
  });

const cloneState = (state) => ({
  viewsById: { ...(state?.viewsById || {}) },
  viewOrder: [...(state?.viewOrder || [])],
  activeViewId: state?.activeViewId || null,
});

const upsertView = (state, viewDefinition) => {
  const normalized = normalizeViewDefinition(viewDefinition);
  if (!normalized) {
    return;
  }
  const viewId = normalized.id;
  state.viewsById[viewId] = normalized;
  state.viewOrder = [viewId, ...state.viewOrder.filter((candidateId) => candidateId !== viewId)];
  state.activeViewId = viewId;
};

const removeView = (state, viewDefinitionId) => {
  const viewId = viewDefinitionId ? String(viewDefinitionId) : '';
  if (!viewId) {
    return;
  }
  delete state.viewsById[viewId];
  state.viewOrder = state.viewOrder.filter((candidateId) => candidateId !== viewId);
  if (state.activeViewId === viewId) {
    state.activeViewId = state.viewOrder[0] || null;
  }
};

export const createViewAuthoringPreviewState = () => ({
  viewsById: {},
  viewOrder: [],
  activeViewId: null,
});

export const materializeViewAuthoringPreview = (state) => {
  const workingState = state || createViewAuthoringPreviewState();
  const orderedViews = workingState.viewOrder
    .map((viewId) => workingState.viewsById[viewId] || null)
    .filter(Boolean);

  const fallbackActiveViewId = orderedViews[0]?.id || null;
  const activeViewId =
    workingState.activeViewId && workingState.viewsById[workingState.activeViewId]
      ? workingState.activeViewId
      : fallbackActiveViewId;

  return {
    activeViewId,
    views: orderedViews,
    currentView: activeViewId ? workingState.viewsById[activeViewId] || null : null,
  };
};

export const applyViewAuthoringToolComplete = (state, payload = {}) => {
  const toolName = String(payload.toolName || '');
  const result = payload.result || {};

  if (toolName === EXPORT_TOOL_NAME && result?.success && result?.dry_run !== true) {
    return createViewAuthoringPreviewState();
  }

  if (!VIEW_AUTHORING_MUTATION_TOOL_NAMES.has(toolName)) {
    return state || createViewAuthoringPreviewState();
  }

  const next = cloneState(state || createViewAuthoringPreviewState());
  if (toolName === 'delete_ontology_view_definition' && result?.success) {
    removeView(next, result?.view_definition_id || result?.view_definition?.id);
    return next;
  }
  upsertView(next, result?.view_definition);
  return next;
};

export const buildViewAuthoringPreviewStateFromEnvironmentSnapshot = (snapshotPayload) => {
  const snapshot = snapshotPayload?.snapshot;
  const viewsPayload = snapshotPayload?.preview || snapshot?.views;
  const dirtyIds = new Set(
    safeArray(viewsPayload?.dirty_view_definition_ids)
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );

  if (dirtyIds.size === 0) {
    return createViewAuthoringPreviewState();
  }

  const dirtyViews = sortViewsByRecency(
    safeArray(viewsPayload?.view_definitions)
      .map((viewDefinition) => normalizeViewDefinition(viewDefinition))
      .filter((viewDefinition) => viewDefinition && dirtyIds.has(viewDefinition.id))
  );

  const next = createViewAuthoringPreviewState();
  dirtyViews.forEach((viewDefinition) => upsertView(next, viewDefinition));
  if (dirtyViews.length > 0) {
    next.activeViewId = dirtyViews[0].id;
  }
  return next;
};
